import React, { useEffect, useMemo, useState } from 'react';
import { useStore } from 'app/context';
import { observer } from 'mobx-react';
import { Table } from './../table/table';
import { ITableColumn, TableModel, TableRow } from './../table/table.model';
import { DocumentVM, DokumentReadState } from 'app/stores/ui/document.ui.store';
import { formatDate } from 'app/utils';
import { AgenturNameCell } from '../shared/AgenturName.cell';
import { FileSize } from '../common/FileSize';
import { runInAction } from 'mobx';
import { DocumentCategoryName } from './DocumentCategoryName';
import { DocumentShareState } from './DocumentShareState';
import { Button } from '../common/Button';
import { Icon } from '../common/Icon';
import { ConfirmModal } from '../common/ConfirmModal';

interface IDocumentList {
	agtId?: number;
	bnrId?: number;
	listTm?: ITableColumn<DocumentVM, number>[];
}

interface IUploads {
	doc: DocumentVM;
}
export const Uploads = observer((props: IUploads) => {
	const doc = props.doc;
	if (doc.uploads.length === 0) {
		return <> </>;
	}
	return (
		<div className="no-wrap">
			{doc.uploads.length} (<FileSize fileSize={doc.uploadSize} />)
		</div>
	);
});

export const DocumentList = observer((props: IDocumentList) => {
	const { documentUiStore, uiStore } = useStore();
	const [isMarkingAllAsRead, setIsMarkingAllAsRead] = useState(false);
	const confirmMarkAllReadModalId = 'confirm-mark-all-read-modal';

	const tm = useMemo(() => {
		const viewDoc = (doc: DocumentVM) => {
			runInAction(() => {
				documentUiStore.setCurrentModalDoc(doc);
				uiStore.showModal(uiStore.modalIds.documentViewModal);
			});
		};

		const tm = new TableModel<DocumentVM, number>();

		let cols: ITableColumn<DocumentVM, number>[];
		if (props.listTm) {
			cols = props.listTm;
		} else {
			cols = [
				{
					label: 'Geteilt von/mit',
					path: 'sharedState',
					render: (row: TableRow<DocumentVM>) => {
						return (
							<>
								<DocumentShareState doc={row.data} docListView={true} />
								{row.data.isSharedBy && !row.data.gelesen && <span className="tag is-blue is-small is-inverted">NEU</span>}
							</>
						);
					},
				},
			];
			if (!props.agtId) {
				cols.push({
					label: 'Agentur',
					path: 'agt.name',
					render: AgenturNameCell,
				});
			}
			cols = cols.concat([
				{
					label: 'Typ',
					path: 'doc.category',
					render: (row: TableRow<DocumentVM>) => {
						return <DocumentCategoryName category={row.data.doc.category} />;
					},
				},
				{
					label: 'Plan',
					path: 'plan.year',
				},
				{
					label: 'Titel',
					path: 'doc.title',
				},
				{
					label: 'Anhänge',
					path: 'uploads.length',
					render: (row: TableRow<DocumentVM>) => {
						return <Uploads doc={row.data} />;
					},
				},
				{
					label: 'Erstellt',
					path: 'doc.created',
					format: (val: any) => {
						return formatDate(val);
					},
				},
				{
					label: 'Geändert',
					path: 'doc.modified',
					format: (val: any) => {
						return formatDate(val);
					},
				},
			]);
		}

		tm.onRowClick = (row: TableRow<DocumentVM>) => {
			viewDoc(row.data);
		};

		tm.setCols(cols);
		tm.sortBy = 'name';
		tm.idProperty = 'id';
		tm.idType = 'string';
		return tm;
	}, [props.agtId, documentUiStore, uiStore, props.listTm]);

	useEffect(() => {
		documentUiStore.findAll().then(() => {
			tm.setRowData(documentUiStore.currentItems);
		});
	}, [documentUiStore, documentUiStore.currentItems, tm]);

	const showMarkAllAsReadButton = !props.listTm; // Only show when not using custom TableModel
	const unreadDocuments = documentUiStore.currentItems.filter((doc) => doc.canToggleShareRead && doc.readState === DokumentReadState.unread);

	const onMarkAllAsRead = async () => {
		if (unreadDocuments.length === 0) {
			return;
		}

		setIsMarkingAllAsRead(true);
		try {
			await documentUiStore.sharedDocumentStore.markAllAsRead(unreadDocuments);
			uiStore.hideModal(confirmMarkAllReadModalId);
		} catch (error) {
			console.error('Failed to mark all documents as read:', error);
		} finally {
			setIsMarkingAllAsRead(false);
		}
	};

	const showConfirmMarkAllRead = () => {
		uiStore.showModal(confirmMarkAllReadModalId);
	};

	return (
		<>
			{showMarkAllAsReadButton && unreadDocuments.length > 0 && (
				<div className="pad-left-1rem pad-bottom-1rem">
					<Button className="button is-primary is-small" onClick={showConfirmMarkAllRead} disabled={isMarkingAllAsRead}>
						<Icon iconClass="check" />
						<span>Alle als gelesen markieren ({unreadDocuments.length})</span>
					</Button>
				</div>
			)}
			<Table tm={tm}></Table>

			<ConfirmModal onConfirm={onMarkAllAsRead} onCancel={() => {}} modalId={confirmMarkAllReadModalId}>
				<p>
					Möchten Sie alle <strong>{unreadDocuments.length}</strong> ungelesenen Dokumente als gelesen markieren?
				</p>
				<p className="has-text-grey is-size-7 mt-2">Diese Aktion kann nicht rückgängig gemacht werden.</p>
			</ConfirmModal>
		</>
	);
});
